// Sliding-window per-value dedup for the continuous-scan beep.
//
// Every raw decode result feeds `observe(texts)`. A text is "fresh" when
// it either hasn't been seen before or hasn't been seen for more than
// `absenceMs` (default 1s). Fresh texts count toward the return value and
// their last-seen timestamp is stamped to `now`. Anything last-seen more
// than `absenceMs` ago is pruned each call so a code can re-beep after
// its "gone" window elapses.
//
// This is intentionally decoupled from `SessionStore.markDecoded`: that
// dedup is permanent-per-session (upload throttling) and would silence
// the beep for the rest of the sweep. The beep wants a sliding window.
//
// Pure: `now` is injected so tests are deterministic. Mirror lives at
// web-vanilla/lib/detection-beeper.js.

export interface DetectionBeeper {
  observe(texts: readonly string[]): number;
  reset(): void;
}

export interface DetectionBeeperOptions {
  absenceMs?: number;
  now?: () => number;
}

export function createDetectionBeeper(options: DetectionBeeperOptions = {}): DetectionBeeper {
  const absenceMs = options.absenceMs ?? 1000;
  const now = options.now ?? (() => performance.now());
  const lastSeen = new Map<string, number>();

  function observe(texts: readonly string[]): number {
    const t = now();
    let fresh = 0;
    for (const text of texts) {
      const prev = lastSeen.get(text);
      if (prev === undefined || t - prev > absenceMs) fresh += 1;
      lastSeen.set(text, t);
    }
    for (const [text, seen] of lastSeen) {
      if (t - seen > absenceMs) lastSeen.delete(text);
    }
    return fresh;
  }

  return {
    observe,
    reset: () => lastSeen.clear(),
  };
}
